import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createSelector, select, Store } from '@ngrx/store';
import { has, isObject } from 'lodash-es';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { AppState } from 'src/app/app.state';
import { ApiServiceResponse, ApiStatus } from 'src/app/core/api/api.models';
import { ApiService } from 'src/app/core/api/api.service';
import { selectLanguage, selectMerchantId } from 'src/app/core/client-configuration/client-configuration.selectors';
import { showToastAction } from 'src/app/core/toast/toast.actions';
import { ToastType } from 'src/app/core/toast/toast.state';
import { DynamicFormControl } from 'src/app/feature/dynamic-form/input/dynamic-form-control';
import { updateBarcodeAction, updateEmailAction } from 'src/app/pages/recover/recover.actions';
import {
	selectRecoverEmail,
	selectRecoverEToken,
	selectRecoverOrderId,
	selectRecoverOToken,
} from 'src/app/pages/recover/recover.selectors';
import { ApiRequestType } from 'src/app/shared/enums/api.enums';
import { postApiRequest } from '../api/api.actions';
import { RefreshMfaInfo } from '../api/responses/refresh-mfa';
import { selectAppConfig } from '../application-config/application-config.selectors';
import { LocaleService } from '../locale/locale.service';
import {
	closeNotificationAction,
	removeNotificationAction,
	showNotificationAction,
} from '../notification/notification.actions';
import { ClickType, NotificationType } from '../notification/notification.state';
import { routeTo } from '../routing/routing.actions';
import {
	changeUserEmailFailed,
	changeUserEmailRequest,
	changeUserEmailSuccess,
	emailReceipt,
	logout,
	openChangeUserEmailDialog,
	openVerifyBarcodeDialog,
	rebuildMfaForOrder,
	rebuildMfaForOrderFailure,
	rebuildMfaForOrderSuccess,
	receiptFailed,
	receiptSentSuccessfully,
	sessionEndedAction,
	setMfaCodeAction,
	startTimerAction,
	stopTimerAction,
} from './user.actions';
import { selectJwt, selectMfaCode, selectMfaExpiration, selectTokens, selectUser, Tokens } from './user.selectors';

export const ONE_MINUTE = 60000;
export const DEFAULT_SESSION_TIME = 1800000;

@Injectable({
	providedIn: 'root',
})
export class UserEffects implements OnDestroy {
	private _destroy$ = new Subject<void>();
	private _mfaExpiration: Date = null;
	private _mfaCode: string = null;
	private _tokens: Tokens = null;
	private _timeUntilExpiration: number = null;
	private _sessionTimeConfig: string;
	// The number of the timeout, similar to an id for the _timer
	private _timer: ReturnType<typeof setTimeout>;
	private _isMFA: boolean;

	/**
	 * Handles routing to a different state.
	 */
	logout$ = createEffect(() =>
		this._actions$.pipe(
			ofType(logout),
			map(() => {
				clearTimeout(this._timer);
				return routeTo({ payload: ['login'] });
			})
		)
	);

	openVerifyBarcodeDialog$ = createEffect(() =>
		this._actions$.pipe(
			ofType(openVerifyBarcodeDialog),
			withLatestFrom(this._store$.select(selectRecoverOrderId), this._store$.select(selectRecoverEmail)),
			map(([action, orderId, email]) => {
				const inputField = new DynamicFormControl({
					autocomplete: 'barcode',
					key: 'last4Barcode',
					label: 'common.barcodelastfour',
					controlType: 'text',
					validators: 'required,maxLength:4',
					type: 'email',
					value: '',
				});
				return showNotificationAction({
					initiator: `API Effects - Capture List Email Found`,
					dialogType: NotificationType.CONFIRM,
					buttonLabel: this._locale.get('common.confirm'),
					hideCancelButton: true,
					title: this._locale.get('common.confirmidentity'),
					message: this._locale.get('common.enterlast4barcode'),
					inputField,
					onClose: (clicked: ClickType, data) => {
						const params = {
							order_id: orderId,
							email,
							barcode: data.last4Barcode,
						};
						this._store$.dispatch(
							updateBarcodeAction({
								barcode: data.last4Barcode,
							})
						);
						this._store$.dispatch(
							postApiRequest({
								requestType: ApiRequestType.UNIFIED_GUEST_LOGIN,
								params,
								customData: {
									suppressModal: true,
								},
							})
						);
					},
				});
			})
		)
	);

	openChangeUserEmailDialog$ = createEffect(() =>
		this._actions$.pipe(
			ofType(openChangeUserEmailDialog),
			map(() => {
				const inputField = new DynamicFormControl({
					autocomplete: 'email',
					key: 'changeEmail',
					label: 'common.email',
					controlType: 'text',
					validators: 'required,email',
					type: 'email',
					value: '',
				});
				return showNotificationAction({
					initiator: `API Effects - Capture List Email Found`,
					dialogType: NotificationType.CONFIRM,
					buttonLabel: this._locale.get('common.confirm'),
					hideCancelButton: true,
					title: this._locale.get('common.updateemailaddress'),
					message: this._locale.get('common.enteryouremail'),
					inputField,
					onClose: (clicked: ClickType, data) => {
						this._store$.dispatch(
							changeUserEmailRequest({
								newEmail: data.changeEmail,
							})
						);
					},
				});
			})
		)
	);

	changeUserEmailRequest$ = createEffect(() =>
		this._actions$.pipe(
			ofType(changeUserEmailRequest),
			withLatestFrom(
				this._store$.select(selectMerchantId),
				this._store$.select(selectRecoverOrderId),
				this._store$.select(selectLanguage),
				this._store$.select(selectUser)
			),
			switchMap(([action, merchantId, orderId, clientLanguage, user]) => {
				const request = {
					request_type: ApiRequestType.CHANGE_CUSTOMER_EMAIL,
					application_id: 1501,
					agent_id: 5,
					machine_id: 1,
					customer_id: user?.user?.details?.[0]?.id,
					new_email: action.newEmail,
					order_id: orderId,
					merchant_id: merchantId,
					locale: clientLanguage,
				};
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.CHANGE_CUSTOMER_EMAIL, request)
					.pipe(
						map((response) => {
							if (response?.SERVICE?.status === ApiStatus.OK) {
								this._store$.dispatch(
									updateEmailAction({
										email: action.newEmail,
									})
								);
								return changeUserEmailSuccess();
							}

							return changeUserEmailFailed();
						}),
						catchError(() => of(changeUserEmailFailed()))
					);
			})
		)
	);

	changeUserEmailSuccess$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(changeUserEmailSuccess),
				withLatestFrom(
					this._store$.pipe(select(selectRecoverEmail)),
					this._store$.pipe(select(selectRecoverOrderId)),
					this._store$.pipe(select(selectRecoverEToken)),
					this._store$.pipe(select(selectRecoverOToken)),
					this._store$.pipe(select(selectJwt))
				),
				map(([action, email, orderId, eToken, oToken, jwt]) => {
					this._store$.dispatch(
						showToastAction({
							message: this._locale.get('common.updateuseremailsuccess'),
							toastType: ToastType.SUCCESS,
						})
					);
					const params = {
						eToken,
						oToken,
						jwt,
						email,
						order_id: orderId,
					};
					this._store$.dispatch(
						postApiRequest({
							requestType: ApiRequestType.BOOKING_PORTAL_LOGIN,
							params,
						})
					);
				})
			),
		{ dispatch: false }
	);

	changeUserEmailFailed$ = createEffect(() =>
		this._actions$.pipe(
			ofType(changeUserEmailFailed),
			map(() => {
				return openChangeUserEmailDialog();
			})
		)
	);

	/**
	 * Rebuilds the MFA for the current order
	 */
	rebuildMfaForOrder$ = createEffect(() =>
		this._actions$.pipe(
			ofType(rebuildMfaForOrder),
			switchMap((action) => {
				// stop mfa timer
				clearTimeout(this._timer);

				const request = {
					oToken: action.oToken,
					eToken: action.eToken,
					pToken: action.pToken,
					mfa_code: action.mfa_code,
				};
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.REBUILD_MFA_FOR_ORDER, request)
					.pipe(
						map((response) => {
							if (has(response, 'SERVICE') && isObject(response.SERVICE)) {
								return rebuildMfaForOrderSuccess({
									mfa_code: response.SERVICE.mfa_code,
									mfa_expiration: response.SERVICE.mfa_expiration,
								});
							}

							return rebuildMfaForOrderFailure();
						}),
						catchError(() => of(rebuildMfaForOrderFailure()))
					);
			}),
			catchError(() => of(rebuildMfaForOrderFailure()))
		)
	);

	emailReceipt$ = createEffect(() =>
		this._actions$.pipe(
			ofType(emailReceipt),
			switchMap((action) => {
				const request = {
					merchant_id: action.merchantId,
					order_id: action.orderId,
					agent_id: '5',
					email_address: action.email,
					resend_flag: '0',
				};
				return this._apiService
					.post<{ SERVICE: ApiServiceResponse }>(ApiRequestType.EMAIL_RECEIPT, request)
					.pipe(
						map((response) => {
							if (has(response, 'SERVICE') && isObject(response.SERVICE)) {
								return receiptSentSuccessfully();
							}

							return receiptFailed();
						}),
						catchError(() => of(receiptFailed()))
					);
			}),
			catchError(() => of(receiptFailed()))
		)
	);

	/**
	 * On RebuildMfaForOrder success, store the new mfa and call PaymentPortalLogin
	 */
	rebuildMfaForOrderSuccess$ = createEffect(() =>
		this._actions$.pipe(
			ofType(rebuildMfaForOrderSuccess),
			withLatestFrom(this._store$.select(selectRecoverOrderId)),
			map(([action, orderId]) => {
				this._storeNewMfaCodeAndStartTimer({
					mfa_code: action.mfa_code,
					mfa_expiration: action.mfa_expiration,
				});
				// restart mfa timer
				this._store$.dispatch(startTimerAction({ isMFA: true }));
				return postApiRequest({
					requestType: ApiRequestType.PAYMENT_PORTAL_LOGIN,
					params: {
						oToken: this._tokens.oToken,
						eToken: this._tokens.eToken,
						order_id: orderId,
						cToken: this._tokens.cToken,
						utToken: this._tokens.utToken,
						uidToken: this._tokens.uidToken,
					},
					customData: {
						disableRouting: true,
					},
				});
			}),
			catchError(() => of(rebuildMfaForOrderFailure()))
		)
	);

	/**
	 * When the RebuildMfaForOrder process failes, log the user out
	 */
	rebuildMfaForOrderFailure$ = createEffect(() =>
		this._actions$.pipe(
			ofType(rebuildMfaForOrderFailure),
			map(() => {
				return logout();
			})
		)
	);

	/**
	 * If _isMFA: true
	 * Starts MFA Timer
	 * Else starts Session Timer
	 */
	startTimer$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(startTimerAction),
				map((action) => {
					this._isMFA = action.isMFA;
					if (action.isMFA) {
						this._calculateTime(true);
					} else {
						this._calculateTime(false);
					}
				})
			),
		{ dispatch: false }
	);

	/**
	 * If _isMFA: true
	 * Starts MFA Timer
	 * Else starts Session Timer
	 */
	stopTimer$ = createEffect(
		() =>
			this._actions$.pipe(
				ofType(stopTimerAction),
				map((action) => {
					clearTimeout(this._timer);
				})
			),
		{ dispatch: false }
	);

	/**
	 * Our effects constructor
	 * @param _actions$ Our actions observable
	 * @param _locale Locale Service
	 * @param _store$ Store
	 * @param _apiService the API service
	 */
	constructor(
		private _actions$: Actions,
		private _locale: LocaleService,
		private _store$: Store<AppState>,
		private _apiService: ApiService
	) {
		this._store$
			.pipe(
				select(
					createSelector(
						selectMfaExpiration,
						selectMfaCode,
						selectTokens,
						selectAppConfig(['session_time']),
						(expiration, code, tokens, sessionTimeConfig) => {
							return { expiration, code, tokens, sessionTimeConfig };
						}
					)
				),
				takeUntil(this._destroy$)
			)
			.subscribe((data) => {
				this._mfaExpiration = data.expiration;
				this._mfaCode = data.code;
				this._tokens = data.tokens;
				this._sessionTimeConfig = data?.sessionTimeConfig?.session_time;
			});
	}

	/**
	 * Called when destroying so we can do some cleanup
	 */
	ngOnDestroy(): void {
		this._destroy$.next();
		this._destroy$.complete();
	}

	/**
	 * Calculates time to display Refresh MFA token modal based on mfa_expiration
	 */
	private _calculateTime(isMFA: boolean): void {
		let endTime;
		let startTime;
		let sessionTime;

		if (isMFA) {
			endTime = this._mfaExpiration.getTime();
			startTime = new Date().getTime();
			sessionTime = endTime - startTime;
		} else {
			sessionTime = this._sessionTimeConfig ?? DEFAULT_SESSION_TIME;
		}

		this._timeUntilExpiration = sessionTime - ONE_MINUTE;
		this._startExpirationTimer(this._showRefreshSessionModal.bind(this), this._timeUntilExpiration);
	}

	/**
	 * Starts _timer to execute callback after time passes
	 * @param callback function to be executed after time elapses
	 * @param time amount of time to elapse before executing callback function
	 */
	private _startExpirationTimer(callback, time): void {
		if (this._timer) {
			clearTimeout(this._timer);
		}
		this._timer = setTimeout(callback, time);
	}

	/**
	 * Displays Refresh modal
	 * If MFA is configured:  MFA session will be extended with new MFA token request
	 * If MFA is not configured: session _timer will be reset based on:
	 * - If session_time App Config is configured OR default _timer (30 mins)
	 * Triggers _timer for current session expiration
	 * If the user does not confirm to continue session, session will be ended
	 */
	private _showRefreshSessionModal(): void {
		clearTimeout(this._timer);
		this._store$.dispatch(removeNotificationAction());
		this._store$.dispatch(
			showNotificationAction({
				initiator: `Session to Expire`,
				dialogType: NotificationType.CONFIRM,
				buttonLabel: this._locale.get('common.confirm'),
				buttonLabelCancel: this._locale.get('common.logout'),
				title: this._locale.get('common.sessiontimeoutwarningtitle'),
				message: this._locale.get('common.sessiontimeoutwarningdescription'),
				onClose: (clicked: ClickType) => {
					clearTimeout(this._timer);
					if (clicked === ClickType.OK) {
						this._isMFA ? this._refreshMfa() : this._refreshTimer();
					} else if (clicked === ClickType.CANCEL) {
						this._store$.dispatch(logout());
					}
				},
			})
		);
		this._startExpirationTimer(this._sessionEnded.bind(this), ONE_MINUTE);
	}

	/**
	 * Sets MFA code and expiration if it was on the request and the response was good
	 * @param action action that fired the request
	 */
	private _storeNewMfaCodeAndStartTimer(action: { mfa_code: string; mfa_expiration: string }): void {
		// refreshMFA response has new mfa_code in response node
		const code = action.mfa_code;
		const expiration = new Date(action.mfa_expiration);

		this._store$.dispatch(setMfaCodeAction({ code, expiration }));
		this._store$.dispatch(startTimerAction({ isMFA: true }));
	}

	/**
	 * Makes a request to get new _mfaCode and _mfaExpiration
	 */
	private _refreshMfa(): void {
		const refreshInfo: RefreshMfaInfo = {
			oToken: this._tokens.oToken,
			cToken: this._tokens.cToken,
			mfa_code: this._mfaCode,
		};

		this._store$.dispatch(
			postApiRequest({
				requestType: ApiRequestType.REFRESH_MFA,
				params: refreshInfo,
			})
		);
	}

	/**
	 * Restart regular Session Timer
	 */
	private _refreshTimer(): void {
		clearTimeout(this._timer);
		this._calculateTime(false);
	}

	/**
	 * Runs when the user has not confirmed to continue session
	 * Clears _timer
	 * Closes Refresh MFA token modal
	 * Update state that session ended
	 * User is logged out and returned to login screen
	 * Show Session Ended modal
	 */
	private _sessionEnded(): void {
		clearTimeout(this._timer);
		this._store$.dispatch(closeNotificationAction({}));
		this._store$.dispatch(sessionEndedAction());
		this._store$.dispatch(logout());
		this._store$.dispatch(
			showNotificationAction({
				initiator: `Session Expired`,
				dialogType: NotificationType.GENERAL,
				buttonLabel: this._locale.get('common.close'),
				title: this._locale.get('common.sessionexpiredtitle'),
				message: this._locale.get('common.sessionexpireddescription'),
			})
		);
	}
}
